/**
 * `<ToolCallAffordance>` — the inline collapsible "the agent did X" affordance (editor-chat.md
 * §2.3).
 *
 * editor-chat.md §2.3: the client renders `tool-call` / `tool-result` events "as inline
 * collapsible 'the agent did X' affordances — transparency: the user sees the agentic
 * retrieval happen". This is that affordance. It renders one `ChatToolCallView` (assembled by
 * `useChatStream` from a `tool-call` + its matching `tool-result`):
 *  - a **friendly action label** like "Searching: 'northstar status'" — the user sees what the
 *    agent is doing in plain English, not the raw `search_hybrid query=northstar status` shape;
 *  - an animated status dot — `running` (pulsing, the tool is in flight), `ok` (steady), or
 *    `error` (the agent saw a tool error and is recovering — NOT a turn failure;
 *    editor-chat.md §2.3);
 *  - the result summary, in a collapsible region (collapsed by default — the conversation is
 *    the focus; the retrieval detail is one click away).
 *
 * Only *summaries* ever reach the client (the backend bounds them — conventions.md §4); this
 * component renders what it is given, it never has the full tool args/results.
 */

import { useState } from 'react';
import type { ChatToolCallView } from './chatTypes';

/** `<ToolCallAffordance>` props — one assembled tool-call view. */
export interface ToolCallAffordanceProps {
  toolCall: ChatToolCallView;
}

/** A short, human label for a tool-call status — drives the status dot's class + text. */
function statusLabel(status: ChatToolCallView['status']): string {
  switch (status) {
    case 'running':
      return 'running';
    case 'ok':
      return 'done';
    case 'error':
      // An error here is the *recoverable* path — the agent was handed the error and is
      // continuing (editor-chat.md §2.3). The label is neutral, not alarming.
      return 'recovered';
  }
}

/**
 * Parse the backend's `argsSummary` string ("k1=v1, k2=v2" or "k1, k2") into a key→value
 * map. Pure; unit-testable. The summary is bounded server-side (conventions.md §4) and may
 * be truncated, so callers should treat missing keys as "unknown" not "absent."
 */
function parseArgsSummary(summary: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of summary.split(',')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq > 0) {
      const key = trimmed.slice(0, eq).trim();
      const val = trimmed.slice(eq + 1).trim();
      out[key] = val;
    }
  }
  return out;
}

/** Quote a value for display in an action label, trimming and ellipsizing long strings. */
function quoteVal(v: string | undefined): string {
  if (!v) return '…';
  const trimmed = v.replace(/^['"]|['"]$/g, '');
  if (trimmed.length > 50) return `"${trimmed.slice(0, 47)}…"`;
  return `"${trimmed}"`;
}

/**
 * Friendly action descriptions per tool. Each maps the agent's tool call to one short
 * human-readable phrase the user sees while the tool runs (and after it completes — the
 * status dot conveys the state transition, the verb stays present-continuous).
 *
 * Missing tools fall back to the raw `toolName argsSummary` rendering. Add entries as
 * new tools come online; an unknown tool degrading to the raw shape is intentional —
 * better than a generic "Running tool…" that hides what's happening.
 */
const FRIENDLY_LABELS: Record<string, (a: Record<string, string>) => string> = {
  // --- search ------------------------------------------------------------
  search_hybrid: (a) => `Searching for ${quoteVal(a.query)}`,
  search_lexical: (a) => `Keyword search for ${quoteVal(a.query)}`,
  search_semantic: (a) => `Semantic search for ${quoteVal(a.query)}`,
  search_chunks: (a) => `Searching passages for ${quoteVal(a.query)}`,
  search_by_metadata: () => 'Filtering notes by metadata',
  // --- retrieve ----------------------------------------------------------
  get_note: () => 'Reading note',
  get_notes_batch: () => 'Loading notes',
  get_note_chunks: () => 'Reading note chunks',
  get_notes_in_range: (a) => {
    const from = a.date_from;
    const to = a.date_to;
    if (from && to) return `Loading notes ${from} → ${to}`;
    if (from) return `Loading notes from ${from}`;
    return 'Loading notes in date range';
  },
  // --- list / orient -----------------------------------------------------
  list_notebooks: () => 'Listing notebooks',
  list_tags: () => 'Listing tags',
  list_entities: (a) =>
    a.prefix ? `Listing entities matching ${quoteVal(a.prefix)}` : 'Listing entities',
  note_titles: () => 'Loading note titles',
  // --- traverse ----------------------------------------------------------
  get_linked_notes: () => 'Following note links',
  get_backlinks: () => 'Finding backlinks',
  get_neighborhood: () => 'Exploring note neighborhood',
  find_path: () => 'Finding path between notes',
  // --- entity / tag drill-down ------------------------------------------
  get_entity: () => 'Looking up entity',
  get_notes_by_entity: () => 'Finding notes about entity',
  get_entity_cooccurrences: () => 'Finding co-occurring entities',
  get_entity_timeline: () => 'Building entity timeline',
  get_notes_by_tag: (a) =>
    a.tag_path ? `Finding notes tagged ${quoteVal(a.tag_path)}` : 'Finding tagged notes',
  get_related_tags: () => 'Finding related tags',
  get_tag_timeline: () => 'Building tag timeline',
  // --- analytics ---------------------------------------------------------
  get_action_items: () => 'Finding action items',
  detect_contradictions: () => 'Detecting contradictions',
  get_attribution_audit: () => 'Auditing transcript attribution',
  // --- write -------------------------------------------------------------
  create_note: () => 'Creating note',
  append_to_note: () => 'Appending to note',
  update_note: () => 'Updating note',
  delete_note: () => 'Deleting note',
  restore_note: () => 'Restoring note',
  set_note_starred: () => 'Updating starred state',
  // --- meta --------------------------------------------------------------
  getting_started: () => 'Loading workflow rules',
};

/** Build the user-facing action label. Checks workflow labels first (the chat-primary
 * surface), then atomic labels, then falls back to the raw "toolName args" string. */
export function friendlyToolLabel(toolName: string, argsSummary: string): string {
  const wf = WORKFLOW_LABELS[toolName];
  if (wf !== undefined) return wf(parseArgsSummary(argsSummary));
  const fn = FRIENDLY_LABELS[toolName];
  if (fn !== undefined) return fn(parseArgsSummary(argsSummary));
  // No friendly mapping — show raw name + args. Future-proofs against new tools the
  // mapping table hasn't been updated for.
  return argsSummary ? `${toolName} ${argsSummary}` : toolName;
}

/**
 * Friendly labels for the WORKFLOW tools (high-level composed tools — see backend
 * `api/core/tools_workflows.py`). These take simple inputs (query / focal / scope)
 * and produce bundled results, so the label is more action-y than the atomics:
 * "Synthesizing X" / "Mapping relationships around Y" rather than "search_hybrid
 * query=…".
 */
const WORKFLOW_LABELS: Record<string, (a: Record<string, string>) => string> = {
  synthesize_topic: (a) => `Synthesizing ${quoteVal(a.query)}`,
  find_relationships: (a) => `Mapping relationships around ${quoteVal(a.focal)}`,
  build_timeline: (a) => `Building timeline of ${quoteVal(a.focal)}`,
  find_notes: (a) =>
    a.query ? `Looking up notes for ${quoteVal(a.query)}` : 'Looking up notes by scope',
  find_quotes: (a) => `Finding quotes about ${quoteVal(a.query)}`,
  whats_on_my_plate: (a) =>
    a.assignee && a.assignee !== '"me"'
      ? `Checking action items (${a.assignee})`
      : 'Checking your open action items',
  find_contradictions: (a) => `Looking for contradictions about ${quoteVal(a.topic)}`,
};

/**
 * The inline tool-call affordance. Collapsed by default — the friendly action label +
 * status dot are always visible; the result summary expands on click.
 */
export function ToolCallAffordance({ toolCall }: ToolCallAffordanceProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasResult = toolCall.resultSummary !== null;
  const friendlyLabel = friendlyToolLabel(toolCall.toolName, toolCall.argsSummary);
  // Workflow tools come back from the backend with `innerTools` populated — the list of
  // atomic primitives the workflow composed server-side. Render as a small subtitle so
  // the user sees "synthesize_topic → search_hybrid, search_chunks" instead of just one
  // opaque tool affordance. Atomic tools have innerTools=null → no subtitle.
  const hasInnerTools = toolCall.innerTools !== null && toolCall.innerTools.length > 0;
  // List-returning tools (searches, backlinks, …) come back with up to 3 note titles — the
  // "what came back" snippet. Guard length defensively even though the backend truncates to
  // ≤64 chars. Non-list results (a read, a write) have resultTitles=null → no snippet.
  const resultTitles =
    toolCall.resultTitles !== null && toolCall.resultTitles.length > 0
      ? toolCall.resultTitles.map((t) => (t.length > 64 ? `${t.slice(0, 63)}…` : t))
      : null;

  return (
    // UX-reconciliation: dual-class with the standalone's `.ch-tool` chrome so the
    // tool affordance visually matches `10_5049f3c3.jsx` (mono inline pill).
    <div
      className={`chat-tool-call chat-tool-call--${toolCall.status} ch-tool`}
      data-testid="chat-tool-call"
    >
      <button
        type="button"
        className="chat-tool-call__header"
        onClick={() => setExpanded((v) => !v)}
        // Only expandable once a result has arrived — while `running` there is nothing to show.
        disabled={!hasResult}
        aria-expanded={expanded}
        title={`${toolCall.toolName}(${toolCall.argsSummary})`}
      >
        <span className={`chat-tool-call__status chat-tool-call__status--${toolCall.status}`} />
        <span className="chat-tool-call__name">{friendlyLabel}</span>
        <span className="chat-tool-call__status-label">{statusLabel(toolCall.status)}</span>
      </button>
      {hasInnerTools && (
        <div
          className="chat-tool-call__composed"
          style={{
            fontSize: '11px',
            color: 'var(--fg-4, var(--fg-3))',
            paddingLeft: '18px',
            marginTop: '2px',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.02em',
          }}
          title="Atomic primitives the workflow composed server-side"
        >
          ↳ {(toolCall.innerTools as string[]).join(' · ')}
        </div>
      )}
      {expanded && hasResult && (
        <div className="chat-tool-call__result">{toolCall.resultSummary}</div>
      )}
      {expanded && resultTitles !== null && (
        // The "what came back" snippet — the real note titles the tool returned, so the
        // user SEES the data flowing, not just that a tool ran. Subtle/mono, same visual
        // vocabulary as the `innerTools` subtitle above.
        <div
          className="chat-tool-call__returned"
          style={{
            fontSize: '11px',
            color: 'var(--fg-4, var(--fg-3))',
            paddingLeft: '18px',
            marginTop: '4px',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.02em',
          }}
          title="Note titles the tool returned"
        >
          returned: {resultTitles.join(' · ')}
        </div>
      )}
    </div>
  );
}
